import type { RootData } from '~/root';
import { useRouteLoaderData } from 'react-router';
import { useMemo } from 'react';
import { useEffectiveDisplayMode } from './use-effective-display-mode';

/**
 * `appDisplayMode` is overridden here rather than left as the server sent it.
 * The loader can only answer from the user agent, which says what kind of
 * device asked for the page but nothing about how much room it has: a desktop
 * browser at 200% zoom has a phone's worth of width and wants the tree that
 * fits it. Overriding at the single point every root-data reader goes through
 * keeps that decision in one place.
 *
 * The server's answer stays the value until hydration, so first paint matches
 * the HTML. Loaders still fetch against the user-agent answer, so a swapped
 * tree may render data shaped for the other one.
 */
export const useRootData = (): RootData => {
    const data = useRouteLoaderData('root') as RootData;
    const appDisplayMode = useEffectiveDisplayMode(data?.appDisplayMode);

    return useMemo(
        () => (data ? { ...data, appDisplayMode } : data),
        [data, appDisplayMode],
    );
};
